@file:OptIn(ExperimentalResourceApi::class)

package ui.license

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import api.CurrencyType
import api.LicenseState
import common.frontend.api.Io
import common.frontend.api.isSuperAdmin
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.ExperimentalResourceApi
import org.ssoft.common.tools.EMPTY
import provider.LocalAccountProvider
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.Navigation
import tk.mallumo.compose.navigation.viewmodel.globalViewModel
import tk.mallumo.compose.navigation.viewmodel.viewModel
import tools.EFieldList
import tools.formatFromRes
import tools.rememberString
import tools.stringRes
import ui.GlobalVM
import ui.widget.ListingItemBody
import ui.widget.ListingSpecs


interface LicenseListingScope {

    val vmGlobal: GlobalVM
    val vm: LicenseListingVM
    val action: (LicenseListingVM.Action) -> Unit
    val nav: Navigation
    val login: Io.User
    val headers: ListingSpecs<Wrapper.Item>

    companion object {

        @Composable
        private fun BodyStruct(
            item: Wrapper.Item,
            approved: @Composable ColumnScope.() -> Unit,
            nonApproved: @Composable ColumnScope.() -> Unit
        ) = ListingItemBody.Composable { modifier: Modifier ->
            CompositionLocalProvider(
                LocalTextStyle provides MaterialTheme.typography.bodyMedium
            ) {
                Column(modifier) {
                    if (LicenseState.IDLE match item.data.licensePending.state) {
                        approved()
                    } else {
                        nonApproved()
                        if (LicenseState.APPROVED match item.data.licenseActive.state) {
                            CompositionLocalProvider(
                                LocalTextStyle provides MaterialTheme.typography.bodySmall.copy(color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.8F))
                            ) {
                                approved()
                            }
                        }
                    }
                }
            }
        }


        private val primaryHeaders = listOf<ListingSpecs.Item<Wrapper.Item>>(
            ListingSpecs.Item(title = Res.string.license_listing__item_id, width = 50.dp, align = TextAlign.Center, lines = 1) { ListingSpecs.trimmedId(it.data.id) },
            ListingSpecs.Item(title = Res.string.license_listing__item_company_name, width = 128.dp) { it.data.name },
            ListingSpecs.Item(title = Res.string.license_listing__item_devices_count, width = 90.dp, align = TextAlign.End) { "${it.data.countDevice}x" },
            ListingSpecs.Item(title = Res.string.license_listing__item_state, width = 128.dp, align = TextAlign.Center) { entry ->
                BodyStruct(
                    item = entry,
                    approved = { Text(LicenseState[entry.data.licenseActive.state].stringRes().rememberString(), Modifier.align(Alignment.End)) },
                    nonApproved = { Text(LicenseState[entry.data.licensePending.state].stringRes().rememberString(), Modifier.align(Alignment.End)) })
            },
            ListingSpecs.Item(title = Res.string.license_listing__item_valid_from, width = 90.dp) { entry ->
                BodyStruct(
                    item = entry,
                    approved = {
                        if (entry.data.licenseActive.start == LocalDate.EMPTY) {
                            Text(
                                text = "-",
                                modifier = Modifier.align(Alignment.CenterHorizontally)
                            )
                        } else {
                            Text(
                                text = entry.data.licenseActive.start.formatFromRes(),
                                modifier = Modifier.align(Alignment.CenterHorizontally)
                            )
                        }
                    },
                    nonApproved = {
                        if (entry.data.licensePending.start == LocalDate.EMPTY) {
                            Text(
                                text = "-",
                                modifier = Modifier.align(Alignment.CenterHorizontally)
                            )
                        } else {
                            Text(
                                text = entry.data.licensePending.start.formatFromRes(),
                                modifier = Modifier.align(Alignment.CenterHorizontally)
                            )
                        }
                    })
            },
            ListingSpecs.Item(title = Res.string.license_listing__item_price_device, width = 90.dp, align = TextAlign.End) { entry ->
                BodyStruct(
                    item = entry,
                    approved = {
                        Text(
                            text = buildString {
                                entry.data.licenseActive.price
                                    .takeIf { LicenseState.APPROVED match entry.data.licenseActive.state }
                                    ?.also { append(it) }
                                    ?: run {
                                        append("-")
                                    }
                                append(" ")
                                append(entry.currency.symbol)
                            },
                            textAlign = TextAlign.End,
                            modifier = Modifier.fillMaxWidth()
                        )
                    },
                    nonApproved = {
                        Text(
                            text = buildString {
                                entry.data.licensePending.price
                                    .takeIf { it > 0 }
                                    ?.also { append(it) }
                                    ?: run {
                                        append("-")
                                    }
                                append(" ")
                                append(entry.currency.symbol)
                            },
                            textAlign = TextAlign.End,
                            modifier = Modifier.fillMaxWidth()
                        )
                    })
            },
            ListingSpecs.Item(title = Res.string.license_listing__item_price_sum, width = 90.dp, align = TextAlign.End) { entry ->
                BodyStruct(
                    item = entry,
                    approved = {
                        Text(
                            text = buildString {
                                (entry.data.licenseActive.price * entry.data.countDevice)
                                    .takeIf { LicenseState.APPROVED match entry.data.licenseActive.state }
                                    ?.takeIf { it > 0 }
                                    ?.also { append(it) }
                                    ?: run {
                                        append("-")
                                    }
                                append(" ")
                                append(entry.currency.symbol)
                            },
                            textAlign = TextAlign.End,
                            modifier = Modifier.fillMaxWidth()
                        )
                    },
                    nonApproved = {
                        Text(
                            text = buildString {
                                (entry.data.licensePending.price * entry.data.countDevice)
                                    .takeIf { it > 0 }
                                    ?.also { append(it) }
                                    ?: run {
                                        append("-")
                                    }
                                append(" ")
                                append(entry.currency.symbol)
                            },
                            textAlign = TextAlign.End,
                            modifier = Modifier.fillMaxWidth()
                        )
                    })
            })
        private val headerSpecsPrimary = ListingSpecs.create(
            *primaryHeaders.toTypedArray()
        )
        private val headerSpecsSuperAdmin = ListingSpecs.create(
            * (primaryHeaders + ListingSpecs.Item(title = Res.string.license_listing__item_price_income, width = 90.dp, align = TextAlign.End) { entry ->
                BodyStruct(
                    item = entry,
                    approved = {
                        Text(
                            text = buildString {
                                (entry.data.licenseActive.price * entry.data.countDevice)
                                    .takeIf { LicenseState.APPROVED match entry.data.licenseActive.state }
                                    ?.takeIf { it > 0 }
                                    ?.let { it.toDouble() * entry.percentage }
                                    ?.asDynamic()
                                    ?.toFixed(2)
                                    ?.toString()
                                    ?.also { append(it) }
                                    ?: run {
                                        append("-")
                                    }
                                append(" ")
                                append(entry.currency.symbol)
                            },
                            textAlign = TextAlign.End,
                            modifier = Modifier.fillMaxWidth()
                        )
                    },
                    nonApproved = {
                        Text(
                            text = buildString {
                                (entry.data.licensePending.price * entry.data.countDevice)
                                    .takeIf { it > 0 }
                                    ?.let { it.toDouble() * entry.percentage }
                                    ?.asDynamic()
                                    ?.toFixed(2)
                                    ?.toString()
                                    ?.also { append(it) }
                                    ?: run {
                                        append("-")
                                    }
                                append(" ")
                                append(entry.currency.symbol)
                            },
                            textAlign = TextAlign.End,
                            modifier = Modifier.fillMaxWidth()
                        )
                    })
            }).toTypedArray()
        )


        @Composable
        fun rememberScope(): LicenseListingScope {
            val account = LocalAccountProvider.current
            val nav = LocalNavigation.current
            val vm = nav.viewModel(LicenseListingVM::class)
            val vmGlobal = globalViewModel(GlobalVM::class)
            val action = LicenseListingVM.Action.rememberAction(vm)


            return remember {

                val headers = if (account.current?.isSuperAdmin() == true) headerSpecsSuperAdmin
                else headerSpecsPrimary

                object : LicenseListingScope {
                    override val vmGlobal: GlobalVM
                        get() = vmGlobal
                    override val vm: LicenseListingVM
                        get() = vm
                    override val action: (LicenseListingVM.Action) -> Unit
                        get() = action
                    override val nav: Navigation
                        get() = nav
                    override val login: Io.User
                        get() = account.current!!
                    override val headers: ListingSpecs<Wrapper.Item>
                        get() = headers

                }.also {
                    vm.init(it)
                }
            }
        }
    }

    class Wrapper(
        val licenses: Io.ServerLicensing
    ) : EFieldList<Io.ServerLicensing.Company, Wrapper.Item>(
        items = licenses.items,
        onBindEntry =
            {
                Item(
                    it,
                    currency = CurrencyType[licenses.currency],
                    percentage = (licenses.percentage.toDouble() / 100.0)
                )
            }
    ) {
        class Item(
            override val data: Io.ServerLicensing.Company,
            override val key: String = data.id.toString(),
            val currency: CurrencyType,
            val percentage: Double,
        ) : Entry<Io.ServerLicensing.Company>
    }
}
